import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Tables } from '@/integrations/supabase/types';
import type { Card } from '@/hooks/useCards';

export type CollectionItem = Tables<'user_collection'> & { card?: Card };

export function useCollection() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['collection', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('user_collection')
        .select('*, card:cards(*)')
        .eq('user_id', user.id)
        .order('added_at', { ascending: false });
      if (error) throw error;
      return (data || []) as CollectionItem[];
    },
    enabled: !!user,
  });
}

export function useAddToCollection() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ cardId, photoUrl }: { cardId: string; photoUrl?: string }) => {
      if (!user) throw new Error('Not authenticated');
      // Check if already in collection
      const { data: existing } = await supabase
        .from('user_collection')
        .select('id, quantity')
        .eq('user_id', user.id)
        .eq('card_id', cardId)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('user_collection')
          .update({ quantity: (existing.quantity || 1) + 1, ...(photoUrl ? { photo_url: photoUrl } : {}) })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_collection')
          .insert({ user_id: user.id, card_id: cardId, quantity: 1, photo_url: photoUrl || null });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collection'] });
    },
  });
}

export function useUpdateCollectionItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, quantity }: { id: string; quantity: number }) => {
      if (quantity <= 0) {
        const { error } = await supabase.from('user_collection').delete().eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('user_collection').update({ quantity }).eq('id', id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collection'] });
    },
  });
}
